import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BdcShipmentDetail } from "../../../types/bdc.type";
import { picklistMatches } from "../controlSheet";
import { baht, pcs } from "../format";
import { CARD_SX, SCROLL_SX } from "../styles";
import Tag from "../Tag";
import AutoAdjustPanel from "./AutoAdjustPanel";
import { buildDeliveryRows, deliveryTotals } from "./delivery";

/**
 * ยอดส่งต่อสินค้า — PO pieces against delivered pieces per SKU (the Shipment board's left table).
 * On a draft with per-store lines each row has เกลี่ย, which opens the auto spread beside it.
 */
export default function DeliveryTab({
  detail,
  editable,
  onSaved,
}: Readonly<{
  detail: BdcShipmentDetail;
  /** A draft with stores: เกลี่ย is offered. */
  editable: boolean;
  onSaved: () => void;
}>) {
  const rows = useMemo(() => buildDeliveryRows(detail), [detail]);
  const totals = deliveryTotals(rows);
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const adjustRow = rows.find((row) => row.barcode === adjusting) ?? null;
  const isX = detail.header.kind === "X";
  const allMatch = detail.items.every(picklistMatches);
  const byBarcode = new Map(detail.items.map((item) => [item.barcode, item]));

  return (
    <Stack direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
      <Card sx={{ ...CARD_SX, flex: "3 1 560px", minWidth: 0 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 1.25 }}>
          {isX ? (
            <Tag tone="warn">PO นี้ไม่มีรายร้าน (ไม่มี PICKLST) — ส่งตามยอด PO</Tag>
          ) : allMatch ? (
            <Tag tone="ok">✓ ยอด PO (ORDS) = ผลรวมรายร้าน (PICKLST) ทุกสินค้า</Tag>
          ) : (
            <Tag tone="crit">✗ ยอด PO ไม่ตรงกับผลรวมรายร้านบางสินค้า</Tag>
          )}
          {totals.changed > 0 ? (
            <Tag tone="warn">
              ปรับยอดแล้ว {pcs(totals.changed)} สินค้า · ส่ง {pcs(totals.delivered)} จาก {pcs(totals.po)} ชิ้น
            </Tag>
          ) : (
            <Typography className="sub">ยังไม่ได้ปรับยอด — ยอดส่ง = ยอด PO</Typography>
          )}
        </Stack>
        <Box sx={SCROLL_SX}>
          <table className="bdc-t" data-testid="bdc-delivery-table">
            <thead>
              <tr>
                <th>รหัส 7-11</th>
                <th>สินค้า</th>
                <th className="n">ชิ้น/ลัง</th>
                <th className="n">สั่ง (PO)</th>
                <th className="n">ส่งจริง</th>
                <th className="n">ต่าง</th>
                <th className="n">ลังเต็ม</th>
                <th className="n">เศษ</th>
                <th className="n">ราคา/ชิ้น</th>
                <th className="n">ยอดเงินส่ง</th>
                {!isX && <th className="c">รายร้าน</th>}
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const item = byBarcode.get(row.barcode);
                return (
                  <tr key={row.barcode} style={row.diff !== 0 ? { background: "#FFF7E8" } : undefined}>
                    <td className="mono">{row.item_code}</td>
                    <td>
                      {row.name}
                      {row.size && <span className="sub"> · {row.size}</span>}
                    </td>
                    <td className="n sub">{row.pcs_per_case}</td>
                    <td className="n">{pcs(row.po)}</td>
                    <td className="n">
                      <b>{pcs(row.delivered)}</b>
                    </td>
                    <td className="n">{row.diff !== 0 ? pcs(row.diff) : ""}</td>
                    <td className="n">{pcs(row.full_cases)}</td>
                    <td className="n">{pcs(row.remainder)}</td>
                    <td className="n">{baht(row.unit_price)}</td>
                    <td className="n">{baht(row.amount)}</td>
                    {!isX && (
                      <td className="c">
                        {item && picklistMatches(item) ? (
                          <Tag tone="ok">✓</Tag>
                        ) : (
                          <Tag tone="crit">{pcs(item?.picklist_qty ?? 0)}</Tag>
                        )}
                      </td>
                    )}
                    {editable && (
                      <td>
                        <Button
                          size="small"
                          variant={adjusting === row.barcode ? "contained" : "outlined"}
                          disabled={row.delivered === 0}
                          onClick={() => setAdjusting(row.barcode)}
                          aria-label={`เกลี่ย ${row.name}`}
                        >
                          เกลี่ย
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>รวม {rows.length} SKU</td>
                <td className="n">{pcs(totals.po)}</td>
                <td className="n">{pcs(totals.delivered)}</td>
                <td className="n">{totals.delivered !== totals.po ? pcs(totals.delivered - totals.po) : ""}</td>
                <td className="n">{pcs(rows.reduce((sum, row) => sum + row.full_cases, 0))}</td>
                <td />
                <td />
                <td className="n">{baht(rows.reduce((sum, row) => sum + row.amount, 0))}</td>
                {!isX && <td />}
                {editable && <td />}
              </tr>
            </tfoot>
          </table>
        </Box>
      </Card>

      {editable && adjustRow && (
        <Box sx={{ flex: "2 1 380px", minWidth: 0 }}>
          <AutoAdjustPanel
            poNumber={detail.header.po_number}
            row={adjustRow}
            onClose={() => setAdjusting(null)}
            onSaved={() => {
              setAdjusting(null);
              onSaved();
            }}
          />
        </Box>
      )}
    </Stack>
  );
}
